import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Briefcase, 
  FileText, 
  Users, 
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  Calendar,
  MessageCircle,
  Star,
  Heart,
  MapPin,
  Clock,
  ChevronRight,
  TrendingUp,
  CheckCircle,
  XCircle,
  Hourglass
} from 'lucide-react'
import { apiRequest } from '@/config/api'

interface Application {
  uuid: string
  position_title: string
  company_name: string
  applied_date: string
  status: string
  location: string
  salary_range: string
  recruiter_name: string
  recruiter_avatar?: string
  last_update: string
  interview_scheduled?: boolean
}

interface Resume {
  uuid: string
  name: string
  uploaded_date: string
  file_url: string
  is_primary: boolean
  applications_count: number
}

interface FollowedRecruiter {
  uuid: string
  name: string
  company: string
  avatar_url?: string
  positions_count: number
  followers_count: number
  last_posted: string
}

export default function CandidateDashboard() {
  const [applications, setApplications] = useState<Application[]>([])
  const [resumes, setResumes] = useState<Resume[]>([])
  const [followedRecruiters, setFollowedRecruiters] = useState<FollowedRecruiter[]>([])
  const [activeTab, setActiveTab] = useState('applications')
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        window.location.href = '/login'
        return
      }

      // Load applications
      const appsResponse = await apiRequest('applications/candidate-applications')
      if (appsResponse.ok) {
        const appsData = await appsResponse.json()
        setApplications(appsData.data?.applications || appsData)
      }

      // Load resumes - Note: This endpoint may need user UUID, temporarily commenting out
      // const resumesResponse = await apiRequest('candidate/resumes')
      // if (resumesResponse.ok) {
      //   const resumesData = await resumesResponse.json()
      //   setResumes(resumesData)
      // }

      // Load followed recruiters - based on backend mounting this should be at root level
      try {
        const recruitersResponse = await apiRequest('candidate/followed-recruiters')
        if (recruitersResponse.ok) {
          const recruitersData = await recruitersResponse.json()
          setFollowedRecruiters(recruitersData)
        } else {
          console.log('Followed recruiters endpoint not found or errored:', recruitersResponse.status)
        }
      } catch (recruitersError) {
        console.log('Error calling followed recruiters:', recruitersError)
        // Temporarily set empty array to prevent UI errors
        setFollowedRecruiters([])
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
      case 'applied':
        return <Hourglass className="w-4 h-4 text-yellow-500" />
      case 'shortlisted':
      case 'interview_scheduled':
        return <CheckCircle className="w-4 h-4 text-blue-500" />
      case 'selected':
      case 'offered':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Hourglass className="w-4 h-4 text-gray-500" />
    }
  }

  const filteredApplications = applications.filter(app => {
    const matchesStatus = filterStatus === 'all' || app.status.toLowerCase() === filterStatus
    const matchesSearch = app.position_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.company_name.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const stats = {
    total_applications: applications.length,
    pending: applications.filter(app => ['submitted', 'applied'].includes(app.status.toLowerCase())).length,
    interviews: applications.filter(app => ['shortlisted', 'interview_scheduled'].includes(app.status.toLowerCase())).length,
    offers: applications.filter(app => ['selected', 'offered'].includes(app.status.toLowerCase())).length
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const handleCreateAlert = () => {
    console.log('Create job alert')
    // TODO: Implement job alert creation
  }

  const handleUploadResume = () => {
    console.log('Upload resume')
    // TODO: Implement resume upload
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Title */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your applications, manage your profile, and discover new opportunities</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 lg:pb-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Applications</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total_applications}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Review</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Interviews</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.interviews}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Star className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Offers</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.offers}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applications'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Applications
              </button>
              <button
                onClick={() => setActiveTab('resumes')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'resumes'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Resume Versions
              </button>
              <button
                onClick={() => setActiveTab('recruiters')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'recruiters'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Following
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'applications' && (
              <div>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search positions or companies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="applied">Applied</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="interview_scheduled">Interview Scheduled</option>
                    <option value="selected">Selected</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                {/* Applications List */}
                <div className="space-y-4">
                  {filteredApplications.map((application) => (
                    <motion.div
                      key={application.uuid}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {application.position_title}
                            </h3>
                            {getStatusIcon(application.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              application.status.toLowerCase() === 'selected' ? 'bg-green-100 text-green-800' :
                              application.status.toLowerCase() === 'rejected' ? 'bg-red-100 text-red-800' :
                              application.status.toLowerCase() === 'shortlisted' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {application.status}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">{application.company_name}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {application.location}
                            </div>
                            <div>Applied: {new Date(application.applied_date).toLocaleDateString()}</div>
                            <div>Last Update: {new Date(application.last_update).toLocaleDateString()}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {application.interview_scheduled && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                              Interview Scheduled
                            </span>
                          )}
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'resumes' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Resume Versions</h2>
                  <button className="btn-primary flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload New Version
                  </button>
                </div>

                <div className="grid gap-4">
                  {resumes.map((resume) => (
                    <motion.div
                      key={resume.uuid}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <FileText className="w-8 h-8 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                              {resume.name}
                              {resume.is_primary && (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                  Primary
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Uploaded: {new Date(resume.uploaded_date).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              Used in {resume.applications_count} applications
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <Eye className="w-5 h-5" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'recruiters' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Following Recruiters</h2>
                  <button className="btn-secondary">
                    Find Recruiters
                  </button>
                </div>

                <div className="grid gap-4">
                  {followedRecruiters.map((recruiter) => (
                    <motion.div
                      key={recruiter.uuid}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                            {recruiter.avatar_url ? (
                              <img 
                                src={recruiter.avatar_url} 
                                alt={recruiter.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <Users className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{recruiter.name}</h3>
                            <p className="text-gray-600">{recruiter.company}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span>{recruiter.positions_count} open positions</span>
                              <span>{recruiter.followers_count} followers</span>
                              <span>Last posted: {new Date(recruiter.last_posted).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-primary-600">
                            <MessageCircle className="w-5 h-5" />
                          </button>
                          <button className="p-2 text-red-400 hover:text-red-600">
                            <Heart className="w-5 h-5 fill-current" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}